/**
 * Types for the Briefs domain
 */

import { Idea } from '../Ideas/types';
import { CalendarEvent } from '../Home/hooks/useCalendar';

export type BriefStatus = 'DRAFT' | 'CAPTURING' | 'EDITING' | 'COMPLETE';

export interface BriefNote {
  id: string;
  content: string;
  tags: string[];
  createdAt: string;
}

/** Trimmed calendar event embedded on list responses for card preview. */
export interface BriefListCalendarEvent {
  id: string;
  eventName: string;
  eventMonth: number | null;
  eventDay: number | null;
  endMonth: number | null;
  endDay: number | null;
  category: string;
  movable: boolean;
  dateYear: number | null;
  status: string;
}

export interface Brief {
  id: string;
  clientId: string;
  title: string | null;
  photoDriveUrl: string | null;
  videoDriveUrl: string | null;
  audioDriveUrl: string | null;
  reelsDriveUrl: string | null;
  mediaShareUrl: string | null;
  status: BriefStatus;
  distilledContext: string | null;
  createdAt: string;
  updatedAt: string | null;
  ideas?: Idea[];
  notes?: BriefNote[];
  /** Full calendar events (with links) attached to this brief. */
  calendarEvents?: CalendarEvent[];
}

/**
 * Transient admin-only payload returned by ``POST /api/briefs/:id/share``.
 * Not persisted on ``Brief``; each Share click mints a fresh magic link
 * (backend upserts the underlying token and extends the 7-day TTL).
 */
export interface BriefShareLink {
  magicLink: string;
  expiresAt: string;
}

export interface BriefListItem extends Omit<Brief, 'ideas' | 'notes' | 'calendarEvents'> {
  ideaCount: number;
  calendarEventCount: number;
  /** Up to 3 attached events for card preview (compact shape). */
  calendarEvents: BriefListCalendarEvent[];
}

/** Lightweight row from the portal production-queue endpoint. */
export interface PortalBriefListItem {
  id: string;
  title: string | null;
  status: BriefStatus;
  ideaCount: number;
  createdAt: string | null;
}

export interface CreateBriefRequest {
  title?: string;
}

export interface UpdateBriefRequest {
  title?: string;
  status?: BriefStatus;
  mediaShareUrl?: string;
  distilledContext?: string;
}

export type ShareBriefResponse = BriefShareLink;

export interface BriefsResponse {
  briefs: BriefListItem[];
}

export interface BriefNotesResponse {
  notes: BriefNote[];
}

export interface AddNoteRequest {
  content: string;
  tags?: string[];
}

export interface GenerateContextResponse {
  distilledContext: string;
}

// Query keys factory
export const briefKeys = {
  all: ['briefs'] as const,
  lists: () => [...briefKeys.all, 'list'] as const,
  list: (clientId: string) => [...briefKeys.lists(), clientId] as const,
  details: () => [...briefKeys.all, 'detail'] as const,
  detail: (id: string) => [...briefKeys.details(), id] as const,
  notes: (briefId: string) => [...briefKeys.all, 'notes', briefId] as const,
  portalList: (clientId: string) =>
    [...briefKeys.all, 'portal-list', clientId] as const,
};
